import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { ComposeModal } from "../../../../../components/dashboard/compose";
import { parseInboxDirection } from "@/features/inbox/filters";
import { sendComposeEmail } from "@/features/inbox/server-fns";
import {
  composeOptionsQueryOptions,
  inboxThreadQueryKey,
  inboxThreadQueryOptions,
} from "@/features/inbox/queries";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { settingsUsageOverviewQueryOptions } from "@/features/settings/queries";

export const Route = createFileRoute("/dashboard/inbox/$id/forward/")({
  validateSearch: (search: Record<string, unknown>) => ({
    domainId: (search.domainId as string) || undefined,
    aliasId: (search.aliasId as string) || undefined,
    direction: parseInboxDirection(search.direction),
    q: (search.q as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: "Forward - Bavimail" }],
  }),
  staleTime: 15_000,
  preloadStaleTime: 15_000,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(composeOptionsQueryOptions());
  },
  component: DashboardInboxForwardRoute,
});

function DashboardInboxForwardRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = Route.useParams();
  const { domainId, aliasId, direction, q } = Route.useSearch();
  const { data: aliases } = useSuspenseQuery(composeOptionsQueryOptions());
  const usageOverviewQuery = useQuery({
    ...settingsUsageOverviewQueryOptions(),
    retry: false,
    throwOnError: false,
  });
  const { data: thread } = useSuspenseQuery(inboxThreadQueryOptions(id));

  return (
    <ComposeModal
      mode="forward"
      aliases={aliases}
      showBavimailFooter={!usageOverviewQuery.data?.subscription.isPaidPlan}
      thread={thread}
      onClose={() =>
        navigate({
          to: "/dashboard/inbox/$id",
          params: { id },
          search: { domainId, aliasId, direction, q },
        })
      }
      onSend={async (input) => {
        const result = await sendComposeEmail({ data: input });
        capturePostHogEvent("email_sent", { mode: "forward" });
        if (result.conversationId) {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: inboxThreadQueryKey(result.conversationId) }),
            queryClient.invalidateQueries({ queryKey: ["inboxThreads"] }),
          ]);
        } else {
          await queryClient.invalidateQueries({ queryKey: ["inboxThreads"] });
        }
        if (result.conversationId) {
          await navigate({
            to: "/dashboard/inbox/$id",
            params: { id: result.conversationId },
            search: { domainId, aliasId, direction, q },
          });
          return;
        }

        await navigate({ to: "/dashboard/inbox", search: { domainId, aliasId, direction, q } });
      }}
    />
  );
}
